import { useState, useEffect, useCallback } from 'react'
import { useSelector } from 'react-redux'
import type { RootState } from '../../store'
import {
  createMailCampaign,
  updateMailCampaign,
  testSendMailCampaign,
  resolveMailAudience,
  sendMailCampaign,
  getMailTemplates,
  type MailCampaignRead,
  type MailTemplate,
  type AudienceResolveRequest,
  type AdminMailCampaignKind,
} from '../../services/adminApi'
import { usePermission } from '../../hooks/usePermission'
import { CustomButton } from '../../components/common/CustomButton'
import { CustomTextInput } from '../../components/common/CustomTextInput'
import { AudienceSelector } from './AudienceSelector'
import { CampaignKindPicker } from './CampaignKindPicker'
import { EmailEditor } from './EmailEditor'
import { EmailPreview } from './EmailPreview'
import { DEFAULT_SENDER, senderName } from './sender'
import { fitsEditor, hasMessage } from './editorSchema'

interface CampaignFormModalProps {
  campaign?: MailCampaignRead | null
  onClose: () => void
  onSave: () => void
}

const EDITOR_CONTENT_TYPES = ['richtext', 'html']

interface FormValues {
  name: string
  subject: string
  fromEmail: string
  templateId: number | ''
  scheduledAt: string
  body: string
}

const keyOf = (v: FormValues) => JSON.stringify(v)

interface Saved {
  campaign: MailCampaignRead
  key: string
}

export function CampaignFormModal({ campaign, onClose, onSave }: CampaignFormModalProps) {
  const isEditing = !!campaign
  const canSendServiceNotice = usePermission('email_campaigns', 'send_service_notice')
  const canSend = usePermission('email_campaigns', 'send')
  const adminEmail = useSelector((state: RootState) => state.admin.email)

  const listmonkEditorType = campaign?.body && !EDITOR_CONTENT_TYPES.includes(campaign.content_type)
  // Opening a message the editor can't represent (tables, custom styles) would rewrite it on the first keystroke.
  const [editorOwnsBody] = useState(() => !campaign?.body || (!listmonkEditorType && fitsEditor(campaign.body)))

  const initial: FormValues = {
    name: campaign?.name ?? '',
    subject: campaign?.subject ?? '',
    fromEmail: campaign?.from_email ?? '',
    templateId: campaign?.template_id ?? '',
    scheduledAt: campaign?.scheduled_at ? new Date(campaign.scheduled_at).toISOString().slice(0, 16) : '',
    body: campaign?.body ?? '',
  }
  const [values, setValues] = useState<FormValues>(initial)
  const set = <K extends keyof FormValues>(key: K, value: FormValues[K]) =>
    setValues((v) => ({ ...v, [key]: value }))

  const [kind, setKind] = useState<AdminMailCampaignKind>(
    campaign?.kind === 'service_notice' ? 'service_notice' : 'marketing'
  )
  const [audience, setAudience] = useState<AudienceResolveRequest | null>(null)
  const [templates, setTemplates] = useState<MailTemplate[]>([])
  const [loadingData, setLoadingData] = useState(true)

  const [savedCampaign, setSavedCampaign] = useState<MailCampaignRead | null>(campaign ?? null)
  const [savedKey, setSavedKey] = useState<string | null>(campaign ? keyOf(initial) : null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const [showTest, setShowTest] = useState(false)
  const [testEmails, setTestEmails] = useState(adminEmail ?? '')
  const [testing, setTesting] = useState(false)
  const [testedKey, setTestedKey] = useState<string | null>(null)

  const [preparingSend, setPreparingSend] = useState(false)
  const [confirmCount, setConfirmCount] = useState<number | null>(null)
  const [confirmInput, setConfirmInput] = useState('')
  const [sending, setSending] = useState(false)

  const currentKey = keyOf(values)
  const dirty = currentKey !== savedKey
  const messageReady = !editorOwnsBody || hasMessage(values.body)
  const canSendNow =
    canSend && (!savedCampaign || savedCampaign.status === 'draft' || savedCampaign.status === 'scheduled')

  useEffect(() => {
    async function loadData() {
      setLoadingData(true)
      try {
        const res = await getMailTemplates()
        if (res.ok && res.data) {
          setTemplates(res.data.templates)
          if (!isEditing) {
            const def = res.data.templates.find((t) => t.is_default)
            if (def) setValues((v) => (v.templateId === '' ? { ...v, templateId: def.id } : v))
          }
        } else {
          setError('Failed to load templates.')
        }
      } catch {
        setError('Failed to load form data')
      } finally {
        setLoadingData(false)
      }
    }
    loadData()
  }, [isEditing])

  const handleAudienceChange = useCallback((req: AudienceResolveRequest | null) => {
    setAudience(req)
  }, [])

  const save = async (): Promise<Saved | null> => {
    const sent = values
    if (!sent.name.trim() || !sent.subject.trim() || sent.templateId === '') {
      setError('Fill in the campaign name, subject line and template.')
      return null
    }
    if (!savedCampaign && !audience) {
      setError('Pick who this goes to before saving.')
      return null
    }
    setSaving(true)
    setError(null)
    try {
      const common = {
        name: sent.name.trim(),
        subject: sent.subject.trim(),
        from_email: sent.fromEmail.trim() || null,
        template_id: sent.templateId as number,
        scheduled_at: sent.scheduledAt ? new Date(sent.scheduledAt).toISOString() : null,
        ...(editorOwnsBody ? { body: sent.body } : {}),
      }
      const res = savedCampaign
        ? await updateMailCampaign(savedCampaign.id, common)
        : await createMailCampaign({ ...common, audience: audience!, kind })
      if (!res.ok || !res.data) {
        setError(res.error?.message || 'Failed to save campaign')
        return null
      }
      const fromEmail = res.data.from_email
      const key = keyOf({ ...sent, fromEmail })
      // Keep anything typed while the request was in flight; it stays unsaved.
      setValues((v) => (v.fromEmail === sent.fromEmail ? { ...v, fromEmail } : v))
      setSavedKey(key)
      setSavedCampaign(res.data)
      onSave()
      return { campaign: res.data, key }
    } catch {
      setError('Failed to save campaign')
      return null
    } finally {
      setSaving(false)
    }
  }

  const ensureSaved = async (): Promise<Saved | null> =>
    savedCampaign && !dirty ? { campaign: savedCampaign, key: currentKey } : save()

  const handleTestSend = async () => {
    const emails = testEmails
      .split(/[\n,;\s]+/)
      .map((e) => e.trim())
      .filter(Boolean)
      .slice(0, 5)
    if (emails.length === 0) {
      setError('Enter at least one email address for the test.')
      return
    }
    setTesting(true)
    setError(null)
    try {
      const saved = await ensureSaved()
      if (!saved) return
      const res = await testSendMailCampaign(saved.campaign.id, { test_emails: emails })
      if (res.ok) {
        setTestedKey(saved.key)
        setNotice(`Test sent to ${emails.join(', ')}. Check that inbox before sending for real.`)
        setShowTest(false)
      } else {
        setError(res.error?.message || 'Test send failed')
      }
    } catch {
      setError('Test send failed')
    } finally {
      setTesting(false)
    }
  }

  const openSendReview = async () => {
    if (!audience) {
      setError('Pick who this goes to before sending.')
      return
    }
    if (!messageReady) {
      setError('Write a message before sending.')
      return
    }
    setPreparingSend(true)
    setError(null)
    try {
      const saved = await ensureSaved()
      if (!saved) return
      const res = await resolveMailAudience(audience, kind)
      if (!res.ok || !res.data) {
        setError(res.error?.message || 'Could not count the audience')
        return
      }
      if (res.data.count === 0) {
        setError('Nobody is in this audience, so there is no one to send to.')
        return
      }
      setConfirmInput('')
      setConfirmCount(res.data.count)
    } catch {
      setError('Could not count the audience')
    } finally {
      setPreparingSend(false)
    }
  }

  const handleSend = async () => {
    if (!savedCampaign || !audience || confirmCount === null) return
    if (dirty) {
      setConfirmCount(null)
      setError('The email changed while it was saving. Review and send again to send the latest version.')
      return
    }
    setSending(true)
    setError(null)
    try {
      const res = await sendMailCampaign(savedCampaign.id, { audience, confirmation_count: confirmCount })
      if (res.ok) {
        onSave()
        onClose()
      } else {
        setConfirmCount(null)
        setError(res.error?.message || 'Failed to send campaign')
      }
    } catch {
      setConfirmCount(null)
      setError('Failed to send campaign')
    } finally {
      setSending(false)
    }
  }

  const selectStyles = `w-full rounded-xl border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] text-gray-900 dark:text-gray-100 px-4 py-2.5 text-base transition-all duration-200 focus:border-[#503864] dark:focus:border-[#7f4f9a] focus:outline-none focus:ring-2 focus:ring-[#503864]/20 dark:focus:ring-[#7f4f9a]/20`
  const labelStyles = 'text-sm font-medium text-gray-700 dark:text-gray-300'
  const hintStyles = 'text-xs text-gray-500 dark:text-gray-400'

  const sender = senderName(values.fromEmail)
  const testedThisVersion = testedKey === currentKey

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="relative flex h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-modal dark:bg-[#1a1520]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-4 border-b border-gray-200 px-6 py-4 dark:border-[#3d2c4f]">
          <div className="flex items-center gap-3">
            <h2 className="font-heading text-xl font-bold text-gray-900 dark:text-gray-100">
              {isEditing ? 'Edit campaign' : 'New campaign'}
            </h2>
            {savedCampaign && (
              <span
                className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                  dirty
                    ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                    : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                }`}
              >
                {dirty ? 'Unsaved changes' : 'Draft saved'}
              </span>
            )}
          </div>
          <button onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto lg:grid lg:grid-cols-2 lg:overflow-hidden">
          <div className="space-y-5 px-6 py-5 lg:overflow-y-auto">
            {error && (
              <div className="rounded-xl bg-red-50 p-4 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">{error}</div>
            )}
            {notice && (
              <div className="flex items-start justify-between gap-3 rounded-xl bg-emerald-50 p-4 text-sm text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300">
                <span>{notice}</span>
                <button onClick={() => setNotice(null)} className="text-emerald-600 hover:text-emerald-800 dark:text-emerald-400" aria-label="Dismiss">
                  ✕
                </button>
              </div>
            )}

            {loadingData ? (
              <div className="flex items-center justify-center py-12">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
              </div>
            ) : (
              <>
                {(canSendServiceNotice || kind === 'service_notice') && (
                  <CampaignKindPicker value={kind} onChange={setKind} locked={!!savedCampaign} />
                )}

                <CustomTextInput
                  label="Campaign name"
                  value={values.name}
                  onChange={(e) => set('name', e.target.value)}
                  placeholder="Only admins see this, e.g. November newsletter"
                  required
                />

                <CustomTextInput
                  label="Subject line"
                  value={values.subject}
                  onChange={(e) => set('subject', e.target.value)}
                  placeholder="e.g. New this week on Barta"
                  required
                />

                <div className="flex flex-col gap-1">
                  <CustomTextInput
                    label="From"
                    value={values.fromEmail}
                    onChange={(e) => set('fromEmail', e.target.value)}
                    placeholder={DEFAULT_SENDER}
                  />
                  <p className={hintStyles}>
                    Inboxes will show <span className="font-semibold text-gray-700 dark:text-gray-200">{sender.name}</span>. Leave
                    blank to send as Barta.
                  </p>
                </div>

                <div className="flex flex-col gap-1.5">
                  <span className={labelStyles}>
                    Message <span className="text-red-500">*</span>
                  </span>
                  {editorOwnsBody ? (
                    <EmailEditor value={values.body} onChange={(html) => set('body', html)} />
                  ) : (
                    <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
                      {listmonkEditorType
                        ? `This message was built in Listmonk's ${campaign?.content_type} editor.`
                        : "This message has layout this editor can't keep, like tables or custom styles."}{' '}
                      Edit it{' '}
                      <a href={campaign?.listmonk_admin_url} target="_blank" rel="noopener noreferrer" className="font-medium underline">
                        in Listmonk
                      </a>{' '}
                      so its layout isn't lost.
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <label className={labelStyles}>
                    Template <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={values.templateId}
                    onChange={(e) => set('templateId', e.target.value === '' ? '' : Number(e.target.value))}
                    className={selectStyles}
                  >
                    <option value="">Select a template...</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                        {t.is_default ? ' (default)' : ''}
                      </option>
                    ))}
                  </select>
                  <p className={hintStyles}>The frame around your message: logo, footer and unsubscribe link.</p>
                </div>

                <AudienceSelector onChange={handleAudienceChange} kind={kind} />

                <div className="flex flex-col gap-1">
                  <label className={labelStyles}>Schedule (optional)</label>
                  <input
                    type="datetime-local"
                    value={values.scheduledAt}
                    onChange={(e) => set('scheduledAt', e.target.value)}
                    min={new Date().toISOString().slice(0, 16)}
                    className={selectStyles}
                  />
                  <p className={hintStyles}>Leave blank to save as a draft and send it yourself.</p>
                </div>
              </>
            )}
          </div>

          <div className="border-t border-gray-200 bg-[#f6f1fa] px-6 py-5 dark:border-[#3d2c4f] dark:bg-[#120e15] lg:overflow-y-auto lg:border-l lg:border-t-0">
            <div className="mb-3 flex items-baseline justify-between">
              <h3 className="font-heading text-lg font-bold text-gray-900 dark:text-gray-100">Preview</h3>
              <span className={hintStyles}>How it lands in the inbox</span>
            </div>
            {savedCampaign ? (
              <EmailPreview
                campaignId={savedCampaign.id}
                subject={values.subject}
                fromEmail={values.fromEmail}
                body={editorOwnsBody ? values.body : undefined}
                templateId={values.templateId === '' ? null : values.templateId}
              />
            ) : (
              <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-[#d9c9ec] bg-white/60 px-8 py-20 text-center dark:border-[#3d2c4f] dark:bg-white/5">
                <svg className="h-10 w-10 text-[#9a6bb8]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.9 5.26a2 2 0 002.2 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <p className="font-medium text-gray-800 dark:text-gray-200">Save the draft to see the real email</p>
                <p className="max-w-xs text-sm text-gray-500 dark:text-gray-400">
                  The preview renders your message inside the template, footer and all, and updates as you type.
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 border-t border-gray-200 px-6 py-3 dark:border-[#3d2c4f]">
          {showTest ? (
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <input
                autoFocus
                value={testEmails}
                onChange={(e) => setTestEmails(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleTestSend()}
                placeholder="you@barta.shop (up to 5)"
                className="min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-[#503864] focus:outline-none dark:border-[#3d2c4f] dark:bg-[#0f0d12] dark:text-gray-100"
              />
              <CustomButton onClick={handleTestSend} loading={testing} variant="secondary">
                Send test
              </CustomButton>
              <CustomButton onClick={() => setShowTest(false)} variant="ghost">
                Cancel
              </CustomButton>
            </div>
          ) : (
            <div className="flex flex-1 items-center gap-3">
              <CustomButton onClick={() => setShowTest(true)} variant="secondary" disabled={loadingData}>
                Send me a test
              </CustomButton>
              {savedCampaign && !testedThisVersion && (
                <span className={hintStyles}>Send yourself a test before it goes out.</span>
              )}
            </div>
          )}

          <div className="ml-auto flex items-center gap-2">
            <CustomButton onClick={onClose} variant="ghost">
              Close
            </CustomButton>
            <CustomButton
              onClick={save}
              loading={saving}
              disabled={loadingData || (!!savedCampaign && !dirty)}
              variant="secondary"
            >
              {savedCampaign ? 'Save changes' : 'Save draft'}
            </CustomButton>
            {canSendNow && (
              <CustomButton
                onClick={openSendReview}
                loading={preparingSend}
                disabled={loadingData || !messageReady}
                title={messageReady ? undefined : 'Write a message first'}
                variant="primary"
              >
                Review and send
              </CustomButton>
            )}
          </div>
        </div>

        {confirmCount !== null && savedCampaign && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/45 p-4" onClick={() => !sending && setConfirmCount(null)}>
            <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-modal dark:bg-[#1f1926]" onClick={(e) => e.stopPropagation()}>
              <p className="font-heading text-5xl font-bold text-[#503864] dark:text-[#d6b8ea]">{confirmCount.toLocaleString()}</p>
              <h3 className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">
                {confirmCount === 1 ? 'person' : 'people'} will get this email
              </h3>

              <dl className="mt-4 space-y-2 rounded-xl bg-gray-50 p-4 text-sm dark:bg-white/5">
                <div className="flex gap-3">
                  <dt className="w-16 shrink-0 text-gray-500 dark:text-gray-400">Subject</dt>
                  <dd className="min-w-0 font-medium text-gray-900 dark:text-gray-100">{savedCampaign.subject}</dd>
                </div>
                <div className="flex gap-3">
                  <dt className="w-16 shrink-0 text-gray-500 dark:text-gray-400">From</dt>
                  <dd className="min-w-0 truncate font-medium text-gray-900 dark:text-gray-100">{savedCampaign.from_email}</dd>
                </div>
              </dl>

              {kind === 'service_notice' && (
                <p className="mt-3 rounded-xl bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-900/20 dark:text-amber-200">
                  Service notice: this reaches people who turned off marketing email.
                </p>
              )}
              {!testedThisVersion && (
                <div className="mt-3 flex items-center justify-between gap-3 rounded-xl bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-900/20 dark:text-amber-200">
                  <span>You haven't sent yourself a test of this version.</span>
                  <button
                    onClick={() => {
                      setConfirmCount(null)
                      setShowTest(true)
                    }}
                    className="shrink-0 font-medium underline"
                  >
                    Test first
                  </button>
                </div>
              )}

              <label className="mt-5 block text-sm text-gray-700 dark:text-gray-300">
                This can't be undone. Type <span className="font-semibold">{confirmCount}</span> to confirm.
              </label>
              <input
                autoFocus
                inputMode="numeric"
                value={confirmInput}
                onChange={(e) => setConfirmInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && confirmInput.trim() === String(confirmCount) && handleSend()}
                className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-base text-gray-900 focus:border-[#503864] focus:outline-none dark:border-[#3d2c4f] dark:bg-[#0f0d12] dark:text-gray-100"
              />
              <div className="mt-5 flex justify-end gap-2">
                <CustomButton onClick={() => setConfirmCount(null)} variant="secondary" disabled={sending}>
                  Cancel
                </CustomButton>
                <CustomButton
                  onClick={handleSend}
                  loading={sending}
                  disabled={confirmInput.trim() !== String(confirmCount)}
                  variant="danger"
                >
                  Send to {confirmCount.toLocaleString()} {confirmCount === 1 ? 'person' : 'people'}
                </CustomButton>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
